import { config } from '../config.ts';
import { pickBanter } from '../lib/banter.ts';
import { summaryInfo } from '../lib/coach.ts';
import { bandText, blockPercent, percentBand } from '../lib/percent.ts';
import { lineIn } from '../lib/teacher.ts';
import { stripTags } from '../lib/text.ts';
import type { Outcome, Progress } from '../types.ts';
import type { Companion } from './companion.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import type { Child } from './dom.ts';
import { tx, txAttr, txText } from './i18n.ts';
import type { Pair } from './i18n.ts';
import { icon, mascot, starsEl } from './kit.ts';
import type { IconName } from './kit.ts';

// Napisy ekranu po lekcji (patch-33): idą za przełącznikiem EN/PL i przepisują się w miejscu (liczby, gwiazdki i serie zostają).
const T = {
  lessonDone: { en: 'Lesson complete!', pl: 'Lekcja zakończona!' },
  reviewDone: { en: 'Review complete!', pl: 'Powtórka zakończona!' },
  starsSpeaking: { en: 'Stars for pronunciation', pl: 'Gwiazdki za wymowę' },
  allStars: { en: 'All stars', pl: 'Wszystkie gwiazdki' },
  newWords: { en: 'New words', pl: 'Nowe słówka' },
  reviewed: { en: 'Words reviewed', pl: 'Powtórzone słówka' },
  wordsTyped: { en: 'Words typed', pl: 'Wpisane słówka' },
  toPractice: { en: 'To practice', pl: 'Do poćwiczenia' },
  streak: { en: 'Day streak', pl: 'Seria dni' },
  backToStart: { en: 'Back to start', pl: 'Wróć na start' },
} as const satisfies Record<string, Pair>;

/** Lekcja kursu v2 na ekranie podsumowania: słowa-cele i tekst zdania po id (do listy „Do poćwiczenia”). */
export interface CourseSummaryInfo {
  words: readonly string[];
  textOf(id: string): string | undefined;
}

export function renderSummary(a: { ctx: AppCtx; mode: 'lesson' | 'review'; course?: CourseSummaryInfo; /** „Powtórka dnia” kursu v2: ile słów i ile poprawnie (bez dużych gwiazdek — powtórka ich nie daje). */ review?: { total: number; correct: number }; outcomes: Outcome[]; before: Progress; after: Progress; companion?: Companion }): HTMLElement {
  const { ctx, outcomes, after, companion } = a;
  // gwiazdki dają zdania do powiedzenia i „Co powiesz?” (to drugie tylko, gdy oceniał je mikrofon — przy dotykaniu kart nie ma gwiazdek)
  const speaking = (o: Outcome): boolean => o.kind === 'speak' || (o.kind === 'say' && o.stars !== undefined);
  const speak = outcomes.filter(speaking);
  const earned = speak.reduce((n, o) => n + (o.stars ?? 0), 0);
  const max = speak.length * 3;
  const cloze = outcomes.filter((o) => o.kind === 'cloze'); // pytania kroku 4 (`quiz`) wprowadzają słowa i nie liczą się do „wpisanych” (SPEC §23.26)
  const clozeOk = cloze.filter((o) => o.correct).length;
  const avg = speak.length ? earned / speak.length : 0;
  const percent = blockPercent(outcomes); // null, gdy żadne zdanie nie było oceniane przez Azure
  // zdania, przy których przeszliśmy dalej bez zaliczenia: średnia gwiazdek i procent ich nie ukryją (np. „fu” zamiast „food”)
  const unpassed = outcomes.filter((o) => (o.kind === 'speak' || o.kind === 'say') && o.passed === false);
  const sentenceText = (id: string): string => a.course?.textOf(id) ?? id;
  const shownStars = unpassed.length > 0 ? Math.min(Math.round(avg), 2) : Math.round(avg);
  const farewell = pickBanter(ctx.banter, 'farewell', after.history.length);

  let spokenFarewell = lineIn(farewell, ctx.teacher?.lang() ?? config.teacher.defaultLang);
  const playFarewell = () => void ctx.player.play(spokenFarewell.id, { text: spokenFarewell.text, lang: spokenFarewell.lang });
  // Pożegnanie mówi tylko bot (nagranie ElevenLabs) — na ekranie go nie ma (patch-31c). Tekst zostaje w drzewie dla czytników ekranu (`sr-only`);
  // jedynie komentarz bota z rozmowy (AI) jest pokazywany, bo nie ma go w nagraniach.
  const farewellEl = h('p', { class: 'farewell sr-only', 'data-testid': 'summary-bubble' }, stripTags(spokenFarewell.text));
  const percentMsg = h('span', { class: 'percent-msg', 'data-testid': 'percent-msg' });
  const langNow = (): 'en' | 'pl' => ctx.teacher?.lang() ?? config.teacher.defaultLang;
  const msgText = (): string => (unpassed.length > 0 || percent === null ? '' : bandText(percentBand(percent), langNow())); // „Prawie! Część zdań…” usunięte — nic nie wnosiło, a „do poćwiczenia” mówi kafelek
  percentMsg.textContent = msgText();
  // zmiana języka na ekranie podsumowania: pożegnanie od razu w nowym języku (głos), komunikat pod procentem też; gdy mówi bot z rozmowy, jego komentarz zostaje
  const offLang = ctx.teacher?.onChange((lang) => {
    if ((farewellEl as { isConnected?: boolean }).isConnected === false) return offLang?.();
    percentMsg.textContent = msgText();
    if (companion) return;
    ctx.player.stop();
    spokenFarewell = lineIn(farewell, lang);
    farewellEl.textContent = stripTags(spokenFarewell.text);
    playFarewell();
  });
  // Bot w zadaniach: krótki komentarz do tej lekcji zamiast gotowego pożegnania. Gdy się nie uda (albo go nie ma) — gotowe pożegnanie z nagraniem, jak zawsze.
  if (companion) {
    const info = summaryInfo({ mode: a.mode, outcomes, percent, sentenceText });
    void companion
      .summary(info, (text) => {
        farewellEl.classList.remove('sr-only');
        farewellEl.textContent = text;
      })
      .then((said) => {
        if (!said && companion.alive()) playFarewell();
      });
  } else playFarewell();

  const tile = (cls: string, ic: IconName, label: Pair, value: Child | Child[], sub?: Child | Child[], testid?: string): HTMLElement =>
    h('div', { class: `sum-tile ${cls}`, 'data-testid': testid },
      h('div', { class: 'st-icon' }, icon(ic)),
      txText(ctx, h('div', { class: 'st-label' }), label),
      value ? h('div', { class: 'st-value' }, ...(Array.isArray(value) ? value : [value])) : null,
      sub ? h('div', { class: 'st-sub' }, ...(Array.isArray(sub) ? sub : [sub])) : null,
    );
  const tiles: HTMLElement[] = [];
  if (speak.length > 0) tiles.push(tile('t-star', 'star', T.starsSpeaking, `${earned} / ${max}`, tx(ctx, { en: `Total: ${after.stars} ⭐`, pl: `Razem: ${after.stars} ⭐` }), 'tile-stars'));
  else tiles.push(tile('t-star', 'star', T.allStars, `${after.stars}`, undefined, 'tile-stars'));
  const newWords = a.course ? [...a.course.words] : null;
  if (a.mode === 'lesson' && newWords) tiles.push(tile('t-book', 'book', T.newWords, cloze.length > 0 ? `${clozeOk} / ${cloze.length}` : `${newWords.length}`, newWords.join(', '), 'tile-words'));
  else if (a.review) tiles.push(tile('t-book', 'book', T.reviewed, `${a.review.correct} / ${a.review.total}`, undefined, 'tile-words'));
  else if (cloze.length > 0) tiles.push(tile('t-book', 'book', T.wordsTyped, `${clozeOk} / ${cloze.length}`, undefined, 'tile-words'));
  if (unpassed.length > 0) tiles.push(tile('t-list', 'list', T.toPractice, '', h('span', { 'data-testid': 'summary-unpassed' }, unpassed.map((o) => sentenceText(o.ref)).join(' · ')), 'tile-practice'));
  if (a.mode === 'lesson') tiles.push(tile('t-fire', 'fire', T.streak, [h('span', { 'data-testid': 'summary-streak' }, `${after.streak}`), ' 🔥'], undefined, 'tile-streak'));

  return h(
    'section',
    { class: 'task summary', 'data-testid': 'summary' },
    h('div', { class: 'summary-top' },
      h('div', { class: 'summary-main' },
        txText(ctx, h('h2', null), a.mode === 'lesson' ? T.lessonDone : T.reviewDone),
        a.review ? null : starsEl(shownStars, 3, 'stars big', 'summary-stars', ctx),
        percent !== null && txAttr(ctx, h('p', { class: 'percent', 'data-testid': 'summary-percent', 'data-percent': percent, 'data-band': percentBand(percent) }, h('strong', { class: 'percent-num' }, `${percent}%`), percentMsg), 'aria-label', { en: `${percent}% pronunciation`, pl: `${percent}% wymowy` }),
        farewellEl,
      ),
      h('div', { class: 'mascot-stage' }, mascot('celebrate')),
    ),
    h('div', { class: 'sum-tiles' }, ...tiles),
    h('div', { class: 'actions' }, button(tx(ctx, T.backToStart), () => { companion?.destroy(); ctx.goHome(); }, { class: 'btn primary big', testid: 'home' })),
  );
}
